import { FuidSchema, expectDefined, nonEmpty, toJSON } from '@utils';
import { z } from 'zod';

import { Compound } from './Compound';
import { Dag } from './Dag';
import { Forest } from './Forest';
import { Graph } from './Graph';
import { Tree } from './Tree';
import type { VisitableGraph } from './interface/VisitableGraph';
import type { EID, NID } from './types';

export const NIDSchema = FuidSchema.transform(s => s as NID);
export const EIDSchema = FuidSchema.transform(s => s as EID);

export interface GraphImportOpts<G, N, E, GI = G, NI = N, EI = E> {
  gSchema?: z.ZodType<G, z.ZodTypeDef, GI>;
  nSchema?: z.ZodType<N, z.ZodTypeDef, NI>;
  eSchema?: z.ZodType<E, z.ZodTypeDef, EI>;
  isCompound?: boolean;
}

// Used for compound graphs
export const ParentSchema = z.preprocess(
  (val, ctx) => {
    if (val === undefined) return val;
    if (typeof val === 'string') {
      return val.split(' ');
    } else {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Expected a string with "source target" format',
        fatal: true
      });
      return z.NEVER;
    }
  },
  z.tuple([NIDSchema, EIDSchema])
);

export const NodeSchema = z
  .object({
    parent: ParentSchema.optional()
  })
  .passthrough();
type NodeSchemaType = z.infer<typeof NodeSchema>;

export const EdgeSchema = z
  .object({
    endpoints: z.preprocess(
      (val, ctx) => {
        if (val === undefined) return val;
        if (typeof val === 'string') {
          return val.split(' ');
        } else {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Expected a string with "parent inEdge" format',
            fatal: true
          });
          return z.NEVER;
        }
      },
      z.tuple([NIDSchema, EIDSchema])
    )
  })
  .passthrough();
type EdgeSchemaType = z.infer<typeof EdgeSchema>;

export const GraphSchema = z
  .object({
    nodes: z.record(NIDSchema, NodeSchema),
    edges: z.record(EIDSchema, EdgeSchema)
  })
  .partial()
  .passthrough();

export function parseWithPath<T>(
  schema: z.ZodType<T>,
  data: unknown,
  path: (string | number)[]
): T {
  try {
    return schema.parse(data);
  } catch (err) {
    if (err instanceof z.ZodError) {
      throw new z.ZodError(
        err.errors.map(e => ({
          ...e,
          path: [...path, ...e.path]
        }))
      );
    }
    throw err;
  }
}

export function stringifyGraph<G, N, E>(graph: VisitableGraph<G, N, E>): string {
  return JSON.stringify(exportGraphToJSON(graph));
}

export function exportGraphToJSON<G, N, E>(graph: VisitableGraph<G, N, E>): unknown {
  const nodes: Record<string, unknown> = {};
  const nodesCount = graph.nodes.size;
  Array.from(graph.nodes)
    .sort()
    .forEach(id => {
      let result: Record<string, unknown> = {};
      const data = toJSON(graph.nodeData(id));
      if (data !== null && typeof data === 'object' && Object.keys(data).length > 0) {
        result = { ...data };
      } else {
        result.data = data;
      }
      if (graph instanceof Compound) {
        const parent = graph.parent(id);
        if (parent !== undefined) {
          const inEdge = expectDefined(graph.inEdge(id));
          result.parent = `${parent} ${inEdge}`;
        }
      }
      nodes[id] = result;
    });

  const edges: Record<string, unknown> = {};
  Array.from(graph.edges)
    .sort()
    .forEach(id => {
      let result: Record<string, unknown> = {};
      const data = toJSON(graph.edgeData(id));
      if (data !== null && typeof data === 'object' && Object.keys(data).length > 0) {
        result = { ...data };
      } else {
        result.data = data;
      }
      result.endpoints = `${graph.source(id)} ${graph.target(id)}`;
      edges[id] = result;
    });

  const data = toJSON(graph.data);

  let result: Record<string, unknown> = {};
  if (data !== null && typeof data === 'object' && Object.keys(data).length > 0) {
    result = { ...data };
  } else {
    result.data = data;
  }
  result.nodes = nodesCount === 0 ? undefined : nodes;
  result.edges = nonEmpty(edges);
  return result;
}

export function importGraphFromJSON<G, N, E>(
  json: unknown,
  opts?: GraphImportOpts<G, N, E>,
  parentPath: (string | number)[] = []
): Graph<G, N, E> | Compound<G, N, E> {
  const parsed = parseWithPath(GraphSchema, json, parentPath);
  const { nodes, edges, ...rest } = parsed;

  const gSchema = opts?.gSchema;
  let graphData: G | undefined;
  if (gSchema !== undefined) {
    if (rest.data !== undefined) {
      graphData = parseWithPath(gSchema, parsed.data, [...parentPath, 'data']);
    } else if (Object.keys(rest).length > 0) {
      graphData = parseWithPath(gSchema, rest, parentPath);
    }
  }

  let graph: Graph<G, N, E> | Compound<G, N, E>;
  if (opts?.isCompound ?? false) {
    graph = new Compound<G, N, E>({ data: graphData });
  } else {
    graph = new Graph<G, N, E>({ data: graphData });
  }

  if (nodes !== undefined) {
    const forestEdges: [EID, NID, NID][] = [];
    for (const id in nodes) {
      const { parent: parentInfo, ...rest } = nodes[id] as NodeSchemaType;

      const nSchema = opts?.nSchema;
      let nodeData: N | undefined;
      if (nSchema !== undefined) {
        if (rest.data !== undefined) {
          nodeData = parseWithPath(nSchema, rest.data, [
            ...parentPath,
            'nodes',
            id,
            'data'
          ]);
        } else if (Object.keys(rest).length > 0) {
          nodeData = parseWithPath(nSchema, rest, [...parentPath, 'nodes', id]);
        }
      }

      if (graph instanceof Compound) {
        if (parentInfo !== undefined) {
          const [parent, edge] = parentInfo;
          forestEdges.push([edge, parent, id]);
        }
      }

      graph.addNode(id, { data: nodeData });
    }

    if (graph instanceof Compound) {
      for (const [newEdge, newParent, child] of forestEdges) {
        graph.moveNode(child, { newParent, newEdge });
      }
    }
  }

  if (edges !== undefined) {
    for (const id in edges) {
      const edge = edges[id];
      const {
        endpoints: [source, target],
        ...rest
      } = edge as EdgeSchemaType;

      const eSchema = opts?.eSchema;
      let edgeData: E | undefined;
      if (eSchema !== undefined) {
        if (rest.data !== undefined) {
          edgeData = parseWithPath(eSchema, rest.data, [
            ...parentPath,
            'edges',
            id,
            'data'
          ]);
        } else if (Object.keys(rest).length > 0) {
          edgeData = parseWithPath(eSchema, rest, [...parentPath, 'edges', id]);
        }
      }

      graph.addEdge(id, source, target, edgeData);
    }
  }

  return graph;
}

export function importTreeFromJSON<G, N, E>(
  data: unknown,
  opts?: GraphImportOpts<G, N, E>
): Tree<G, N, E> {
  const graph = importGraphFromJSON<G, N, E>(data, opts) as Graph<G, N, E>;
  return new Tree({ graph });
}

export function importCompoundFromJSON<G, N, E>(
  json: unknown,
  opts?: GraphImportOpts<G, N, E>,
  parentPath: (string | number)[] = []
): Compound<G, N, E> {
  return importGraphFromJSON(json, { ...opts, isCompound: true }, parentPath) as Compound<
    G,
    N,
    E
  >;
}

export function importDAGFromJSON<G, N, E>(
  json: unknown,
  opts?: GraphImportOpts<G, N, E>
): Dag<G, N, E> {
  const graph = importGraphFromJSON<G, N, E>(json, opts) as Graph<G, N, E>;
  return new Dag<G, N, E>({ graph });
}

export function importForestFromJSON<G, N, E>(
  data: unknown,
  opts?: GraphImportOpts<G, N, E>
): Forest<G, N, E> {
  const graph = importGraphFromJSON<G, N, E>(data, opts) as Graph<G, N, E>;
  return new Forest({ graph });
}
